import {
  ChangeEvent,
  FunctionComponent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { ChannelContext } from '../../../context';
import {
  Event,
  EventLocation,
  EventLocationType,
  EventStatus,
  EventType,
  EventUserStatus,
  OrganisationTag,
  OrganisationTagType,
  useEventCreateMutation,
  useLazyEventParticipantsQuery,
  useLazyOrganisationUsersQuery,
  UserPreview,
} from '../../../services';
import {
  AutoComplete,
  Button,
  ButtonType,
  ConfirmationModal,
  FileAcceptType,
  FilePreview,
  FileUpload,
  GalleryItem,
  InputField,
  isKeyboardFocus,
  isUrl,
  LocationInputField,
  markdownToHtml,
  Modal,
  PublishToggle,
  Radio,
  ScheduleInput,
  ScheduleInputVariant,
  Select,
  SelectItem,
  Switch,
  TagsPicker,
  TextAreaField,
  TextEditor,
  TextEditorVariant,
  useAppDispatch,
  useAppSelector,
  UserDisplayItem,
  useRefreshFeeds,
  useTags,
} from '../../../shared';
import { eventEdit, updateCardEvent } from '../../../slices';

import classes from './CreateEventModal.module.scss';

interface CreateEventModalProps {
  isOpened: boolean;
  onClose: () => void;
  event?: Event;
}

interface BuildFieldsParams {
  startAt: Date;
  status?: EventStatus;
}

const DEADLINE_DISABLED_THRESHOLD_MS = 5 * 60 * 1000;

const SCHEDULED_DISABLED_THRESHOLD_MS = 5 * 60 * 1000;

export const CreateEventModal: FunctionComponent<CreateEventModalProps> = ({
  isOpened,
  onClose,
  event,
}) => {
  const { t } = useTranslation();

  const { t: ctxT } = useTranslation('', { keyPrefix: 'createEvent' });

  const dispatch = useAppDispatch();

  const [eventCreate] = useEventCreateMutation();

  const [organisationUsers] = useLazyOrganisationUsersQuery();

  const [eventParticipants] = useLazyEventParticipantsQuery();

  const { refreshFeeds } = useRefreshFeeds({ scrollTop: false });

  const { hasEventTags } = useTags();

  const { channels } = useAppSelector(({ channels }) => channels);

  const { id: channelId } = useContext(ChannelContext).channel;

  const {
    id: eventId = 0,
    status: currentStatus = EventStatus.PUBLISHED,
    participantCountHost = 0,
    type: eventType = EventType.DEFAULT,
    name: eventName = '',
    description: eventDescription = '',
    startAt: eventStartAt = null,
    endAt: eventEndAt = null,
    participantsCapacity: eventParticipantsCapacity = 0,
    showParticipants: eventShowParticipants = false,
    participantsRegistrationDeadlineEndAt: eventParticipantsRegistrationDeadlineEndAt = null,
    reminderHoursBeforeStart: eventReminderHoursBeforeStart = null,
    image: eventImage = null,
    allowWaitlist: eventAllowWaitlist = false,
    organisationTags: eventOrganisationTags = [],
    location: eventLocation = { type: EventLocationType.NONE },
    externalUrl: eventExternalUrl = null,
    externalLabel: eventExternalLabel = null,
    channels: eventChannels = [],
  } = { ...event };

  const { allowToEditChannels = true } = { ...event?.permissions };

  const isEditMode = useMemo(() => Boolean(eventId), [eventId]);

  const [fileLoading, setFileLoading] = useState(false);

  const [loading, setLoading] = useState(false);

  const [gallery, setGallery] = useState<GalleryItem[]>(eventImage ? [{ image: eventImage }] : []);

  const [type, setType] = useState<EventType>(eventType);

  const [name, setName] = useState<string>(eventName);

  const [description, setDescription] = useState<string>(eventDescription);

  const [startAt, setStartAt] = useState<Date | null>(eventStartAt ? new Date(eventStartAt) : null);

  const [endAt, setEndAt] = useState<Date | null>(eventEndAt ? new Date(eventEndAt) : null);

  const [organisationTags, setOrganisationTags] =
    useState<OrganisationTag[]>(eventOrganisationTags);

  const [participantsRegistrationDeadlineEndAt, setParticipantsRegistrationDeadlineEndAt] =
    useState<Date | null>(
      eventParticipantsRegistrationDeadlineEndAt
        ? new Date(eventParticipantsRegistrationDeadlineEndAt)
        : null
    );

  const [allowWaitlist, setAllowWaitlist] = useState<boolean>(eventAllowWaitlist);

  const [virtualUrlError, setVirtualUrlError] = useState(false);

  const hasCapacity = useMemo(
    () => Boolean(eventParticipantsCapacity > 0),
    [eventParticipantsCapacity]
  );

  const [participantsCapacity, setParticipantsCapacity] = useState<number>(
    hasCapacity ? eventParticipantsCapacity : 1
  );

  const [capacityLimitEnabled, setCapacityLimitEnabled] = useState<boolean>(hasCapacity);

  const [showParticipants, setShowParticipants] = useState<boolean>(eventShowParticipants);

  const [reminderHoursBeforeStart, setReminderHoursBeforeStart] = useState<number>(
    eventReminderHoursBeforeStart ?? 24
  );

  const [reminderEnabled, setReminderEnabled] = useState<boolean>(
    Boolean(eventReminderHoursBeforeStart)
  );

  const [hosts, setHosts] = useState<UserPreview[]>([]);

  const [hasHostsLoaded, setHasHostsLoaded] = useState(!isEditMode || !participantCountHost);

  const [editStatus, setEditStatus] = useState<EventStatus>(currentStatus);

  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);

  const [location, setLocation] = useState<EventLocation>(eventLocation);

  const [externalLabel, setExternalLabel] = useState<string>(eventExternalLabel ?? '');

  const [externalUrl, setExternalUrl] = useState<string>(eventExternalUrl ?? '');

  const [externalUrlError, setExternalUrlError] = useState(false);

  const [channelIds, setChannelIds] = useState<number[]>(
    isEditMode ? eventChannels.map(({ id }) => id) : [channelId]
  );

  const isExternalUrl = type === EventType.EXTERNAL_URL;

  const hostsFetchQuery = useCallback(
    (query: string, page?: number) => organisationUsers({ page, filters: { query } }),
    [organisationUsers]
  );

  const hostItemTemplate = useCallback((user: UserPreview) => <UserDisplayItem {...user} />, []);

  const onVirtualUrlChange = useCallback(({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    setVirtualUrlError(false);
    setLocation((locationPrev) => ({ ...locationPrev, virtualUrl: value }));
  }, []);

  const onVirtualUrlBlur = useCallback(() => {
    if (location.virtualUrl && !isUrl(location.virtualUrl)) {
      setVirtualUrlError(true);
    }
  }, [location.virtualUrl]);

  const onExternalUrlChange = useCallback(
    ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
      setExternalUrlError(false);
      setExternalUrl(value);
    },
    []
  );

  const onExternalUrlBlur = useCallback(() => {
    if (externalUrl && !isUrl(externalUrl)) {
      setExternalUrlError(true);
    }
  }, [externalUrl]);

  const [scheduledAt, setScheduledAt] = useState<Date | null>(
    event?.scheduledAt ? new Date(event.scheduledAt) : null
  );

  const isDeadlineDisabled = useMemo(() => {
    if (!startAt) {
      return true;
    }

    return startAt.getTime() - Date.now() < DEADLINE_DISABLED_THRESHOLD_MS;
  }, [startAt]);

  const isScheduledDisabled = useMemo(() => {
    if (!startAt) {
      return true;
    }

    return startAt.getTime() - Date.now() < SCHEDULED_DISABLED_THRESHOLD_MS;
  }, [startAt]);

  const channelSelectItems = useMemo<SelectItem<number>[]>(
    () => channels.map(({ id, name }) => ({ id, title: name })),
    [channels]
  );

  useEffect(() => {
    if (startAt && endAt && endAt <= startAt) {
      setEndAt(null);
    }
  }, [startAt, endAt]);

  useEffect(() => {
    if (isDeadlineDisabled) {
      setParticipantsRegistrationDeadlineEndAt(null);
      return;
    }

    if (
      startAt &&
      participantsRegistrationDeadlineEndAt &&
      participantsRegistrationDeadlineEndAt >= startAt
    ) {
      setParticipantsRegistrationDeadlineEndAt(null);
    }
  }, [startAt, participantsRegistrationDeadlineEndAt, isDeadlineDisabled]);

  useEffect(() => {
    if (isScheduledDisabled) {
      setScheduledAt(null);
      return;
    }

    if (startAt && scheduledAt && scheduledAt >= startAt) {
      setScheduledAt(null);
    }
  }, [startAt, scheduledAt, isScheduledDisabled]);

  useEffect(() => {
    if (!isEditMode || !eventId || !participantCountHost) {
      return;
    }

    const loadHosts = async () => {
      try {
        const { items } = await eventParticipants({
          eventId,
          filters: { participantStatuses: [EventUserStatus.HOST] },
          size: participantCountHost,
        }).unwrap();

        setHosts(items);
        setHasHostsLoaded(true);
      } catch {
        toast.error(t('common.error-message'));
      }
    };

    loadHosts();
  }, [eventId, eventParticipants, isEditMode, participantCountHost, t]);

  const buildFields = useCallback(
    ({ startAt, status }: BuildFieldsParams) => {
      const eventStatus = status ?? (scheduledAt ? EventStatus.UNPUBLISHED : EventStatus.PUBLISHED);

      const deadline = participantsRegistrationDeadlineEndAt?.toISOString() ?? null;

      const capacity = capacityLimitEnabled && !isExternalUrl ? participantsCapacity : 0;

      const reminder = reminderEnabled && !isExternalUrl ? reminderHoursBeforeStart : null;

      const locationFields =
        location.type === EventLocationType.IN_PERSON
          ? { address: location.address }
          : location.type === EventLocationType.VIRTUAL
          ? { virtualUrl: location.virtualUrl }
          : undefined;

      return {
        type,
        name,
        startAt: startAt.toISOString(),
        status: eventStatus,
        description,
        endAt: endAt?.toISOString() ?? null,
        scheduledAt: scheduledAt?.toISOString() ?? null,
        imageId: gallery[0]?.image?.id ?? null,
        participantsRegistrationDeadlineEndAt: deadline,
        participantsCapacity: capacity,
        showParticipants: !isExternalUrl && showParticipants,
        reminderHoursBeforeStart: reminder,
        allowWaitlist: !isExternalUrl && capacityLimitEnabled && allowWaitlist,
        organisationTagIds: organisationTags.map(({ id }) => id),
        hostIds: hosts.map(({ id }) => id),
        location: locationFields,
        externalUrl: isExternalUrl ? externalUrl : null,
        externalLabel: isExternalUrl ? externalLabel : null,
        channelIds,
      };
    },
    [
      allowWaitlist,
      capacityLimitEnabled,
      channelIds,
      description,
      endAt,
      externalLabel,
      externalUrl,
      gallery,
      hosts,
      isExternalUrl,
      location.address,
      location.type,
      location.virtualUrl,
      name,
      organisationTags,
      participantsCapacity,
      participantsRegistrationDeadlineEndAt,
      reminderEnabled,
      reminderHoursBeforeStart,
      scheduledAt,
      showParticipants,
      type,
    ]
  );

  const isFormValid = useCallback(() => {
    if (!name || !startAt) {
      toast.error(ctxT('validation-error'));
      return false;
    }

    if (location.type === EventLocationType.VIRTUAL && !isUrl(location.virtualUrl ?? '')) {
      setVirtualUrlError(true);
      toast.error(t('common.url-invalid'));
      return false;
    }

    if (isExternalUrl && !externalLabel.trim()) {
      toast.error(ctxT('validation-error'));
      return false;
    }

    if (isExternalUrl && !isUrl(externalUrl)) {
      setExternalUrlError(true);
      toast.error(t('common.url-invalid'));
      return false;
    }

    return true;
  }, [
    ctxT,
    externalLabel,
    externalUrl,
    isExternalUrl,
    location.type,
    location.virtualUrl,
    name,
    startAt,
    t,
  ]);

  const onCreate = useCallback(
    async (status?: EventStatus) => {
      if (!isFormValid() || !startAt) {
        return;
      }

      setLoading(true);

      try {
        const { payload } = await eventCreate({
          channelId,
          fields: buildFields({ startAt, status }),
        }).unwrap();

        if (!payload) {
          toast.error(ctxT('error'));
          return;
        }

        toast.success(ctxT('success'));
        refreshFeeds();
        onClose();
      } catch {
        toast.error(ctxT('error'));
      } finally {
        setLoading(false);
      }
    },
    [buildFields, channelId, ctxT, eventCreate, isFormValid, onClose, refreshFeeds, startAt]
  );

  const onSave = useCallback(async () => {
    if (!eventId || !isFormValid() || !startAt) {
      return;
    }

    setLoading(true);

    const { type, participantsCapacity, allowWaitlist, ...fields } = buildFields({
      startAt,
      status: editStatus,
    });

    try {
      await dispatch(eventEdit({ channelId, eventId, fields })).unwrap();

      dispatch(updateCardEvent({ channelId, eventId }));

      toast.success(ctxT('edit-success'));
      onClose();
    } catch {
      toast.error(ctxT('edit-error'));
    } finally {
      setLoading(false);
    }
  }, [buildFields, channelId, ctxT, dispatch, editStatus, eventId, isFormValid, onClose, startAt]);

  const onPublishToggle = useCallback(() => {
    const isPublishing = editStatus !== EventStatus.PUBLISHED;

    setEditStatus(isPublishing ? EventStatus.PUBLISHED : EventStatus.UNPUBLISHED);

    if (isPublishing) {
      setScheduledAt(null);
    }
  }, [editStatus]);

  const hasUnsavedContent = useMemo(() => {
    if (isEditMode) {
      return true;
    }

    return [
      name,
      description,
      gallery.length,
      startAt,
      endAt,
      organisationTags.length,
      hosts.length,
      location.address,
      location.virtualUrl,
      externalLabel,
      externalUrl,
    ].some(Boolean);
  }, [
    description,
    endAt,
    externalLabel,
    externalUrl,
    gallery,
    hosts,
    isEditMode,
    location.address,
    location.virtualUrl,
    name,
    organisationTags,
    startAt,
  ]);

  const onCloseHandler = useCallback(() => {
    if (hasUnsavedContent) {
      setShowCloseConfirmation(true);
      return;
    }

    onClose();
  }, [hasUnsavedContent, onClose]);

  const primaryButton = useMemo(() => {
    const isDisabled = fileLoading || loading || !channelIds.length;

    if (isEditMode) {
      return (
        <Button
          type={ButtonType.primary}
          label={t('common.save')}
          onClick={onSave}
          disabled={isDisabled || !hasHostsLoaded}
        />
      );
    }

    return (
      <Button
        type={ButtonType.primary}
        label={t(`common.${scheduledAt ? 'schedule' : 'publish'}`)}
        onClick={() => onCreate()}
        disabled={isDisabled}
      />
    );
  }, [
    channelIds,
    fileLoading,
    hasHostsLoaded,
    isEditMode,
    loading,
    onCreate,
    onSave,
    scheduledAt,
    t,
  ]);

  const descriptionContent = useMemo(
    () =>
      markdownToHtml({
        markdown: description
          .split('\n')
          .map((line) => `<p>${line}</p>`)
          .join(''),
        boldTag: 'strong',
        italicTag: 'em',
      }),
    [description]
  );

  const body = useMemo(() => {
    return (
      <div className={classes['create-event']}>
        <FileUpload
          galleryItems={gallery}
          setGalleryItems={setGallery}
          loading={fileLoading}
          setLoading={setFileLoading}
          fileAcceptTypes={[FileAcceptType.IMAGE]}
          uploadLimit={1}
        />

        <FilePreview galleryItems={gallery} setGalleryItems={setGallery} />

        <TextAreaField
          label={ctxT('name')}
          value={name}
          onChange={({ target: { value } }) => setName(value)}
          maxLength={360}
        />

        <TextEditor
          variant={TextEditorVariant.EVENT}
          className={classes['create-event__description']}
          content={descriptionContent}
          onUpdateHandler={(value) => setDescription(value as string)}
          placeholder={ctxT('description')}
        />

        <div className={classes['create-event__row']}>
          <ScheduleInput
            inputLabel={ctxT('start-time')}
            time={startAt}
            setTime={setStartAt}
            className={classes['create-event__date-field']}
            variant={ScheduleInputVariant.INPUT}
          />

          <ScheduleInput
            inputLabel={ctxT('end-time')}
            time={endAt}
            setTime={setEndAt}
            minDate={startAt}
            className={classes['create-event__date-field']}
            disabled={!startAt}
            variant={ScheduleInputVariant.INPUT}
          />
        </div>

        <div className={classes['create-event__row']}>
          <ScheduleInput
            inputLabel={ctxT('deadline')}
            time={participantsRegistrationDeadlineEndAt}
            setTime={setParticipantsRegistrationDeadlineEndAt}
            maxDate={startAt}
            className={classes['create-event__date-field']}
            disabled={isDeadlineDisabled}
            variant={ScheduleInputVariant.INPUT}
          />
        </div>

        {hasEventTags && (
          <TagsPicker
            type={OrganisationTagType.EVENT}
            tags={organisationTags}
            setTags={setOrganisationTags}
            hideSubTitle
          />
        )}

        <AutoComplete
          propToIdentify={'id'}
          propToDisplay={'screenName'}
          value={hosts}
          fetchQuery={hostsFetchQuery}
          itemTemplate={hostItemTemplate}
          placeholder={ctxT('hosts')}
          onChange={setHosts}
        />

        <div className={classes['create-event__location']}>
          <div className={classes['create-event__row']}>
            <Radio
              label={ctxT('no-location')}
              checked={location.type === EventLocationType.NONE}
              name={'event-location'}
              onChange={() =>
                setLocation((locationPrev) => ({
                  ...locationPrev,
                  type: EventLocationType.NONE,
                }))
              }
            />
            <Radio
              label={ctxT('in-person')}
              checked={location.type === EventLocationType.IN_PERSON}
              name={'event-location'}
              onChange={() =>
                setLocation((locationPrev) => ({
                  ...locationPrev,
                  type: EventLocationType.IN_PERSON,
                }))
              }
            />
            <Radio
              label={ctxT('virtual')}
              checked={location.type === EventLocationType.VIRTUAL}
              name={'event-location'}
              onChange={() =>
                setLocation((locationPrev) => ({
                  ...locationPrev,
                  type: EventLocationType.VIRTUAL,
                }))
              }
            />
          </div>

          {location.type === EventLocationType.IN_PERSON && (
            <LocationInputField
              label={ctxT('address')}
              value={location.address ?? ''}
              onChange={({ target: { value } }) =>
                setLocation((locationPrev) => ({ ...locationPrev, address: value }))
              }
              autoFocus={!isKeyboardFocus()}
            />
          )}

          {location.type === EventLocationType.VIRTUAL && (
            <InputField
              label={ctxT('meet-link')}
              value={location.virtualUrl ?? ''}
              onChange={onVirtualUrlChange}
              onBlur={onVirtualUrlBlur}
              placeholder={'https://'}
              autoFocus={!isKeyboardFocus()}
              {...(virtualUrlError && { error: true, errorMessage: t('common.url-invalid') })}
            />
          )}
        </div>

        <div className={classes['create-event__type']}>
          <span className={classes['create-event__type-label']}>{ctxT('type')}</span>

          <div className={classes['create-event__row']}>
            <Radio
              label={ctxT('type-default')}
              checked={type === EventType.DEFAULT}
              name={'event-type'}
              disabled={isEditMode}
              onChange={() => setType(EventType.DEFAULT)}
            />
            <Radio
              label={ctxT('type-custom')}
              checked={isExternalUrl}
              name={'event-type'}
              disabled={isEditMode}
              onChange={() => setType(EventType.EXTERNAL_URL)}
            />
          </div>

          {isExternalUrl && (
            <>
              <InputField
                label={ctxT('external-label')}
                value={externalLabel}
                onChange={({ target: { value } }) => setExternalLabel(value)}
                maxLength={40}
                autoFocus={!isKeyboardFocus()}
              />

              <InputField
                label={ctxT('external-url')}
                value={externalUrl}
                onChange={onExternalUrlChange}
                onBlur={onExternalUrlBlur}
                placeholder={'https://'}
                {...(externalUrlError && { error: true, errorMessage: t('common.url-invalid') })}
              />
            </>
          )}
        </div>

        {!isExternalUrl && (
          <>
            <Switch
              label={ctxT('show-participants')}
              checked={showParticipants}
              onChange={setShowParticipants}
              switchFirst
            />

            <div className={classes['create-event__row']}>
              <Switch
                className={classes['create-event__switch']}
                label={ctxT('capacity')}
                checked={capacityLimitEnabled}
                disabled={isEditMode}
                onChange={setCapacityLimitEnabled}
                switchFirst
              />

              {capacityLimitEnabled && (
                <InputField
                  type={'number'}
                  min={1}
                  value={participantsCapacity.toString()}
                  className={classes['create-event__number']}
                  inputClassName={classes['create-event__number-input']}
                  disabled={isEditMode}
                  onChange={({ target: { value } }) =>
                    setParticipantsCapacity(Math.max(1, Number(value)))
                  }
                />
              )}
            </div>

            {capacityLimitEnabled && (
              <Switch
                label={ctxT('allow-waitlist')}
                checked={allowWaitlist}
                disabled={isEditMode}
                onChange={setAllowWaitlist}
                switchFirst
              />
            )}

            <div className={classes['create-event__row']}>
              <Switch
                className={classes['create-event__switch']}
                label={ctxT('reminder')}
                checked={reminderEnabled}
                onChange={setReminderEnabled}
                switchFirst
              />

              {reminderEnabled && (
                <InputField
                  type={'number'}
                  min={0}
                  value={reminderHoursBeforeStart.toString()}
                  className={classes['create-event__number']}
                  inputClassName={classes['create-event__number-input']}
                  onChange={({ target: { value } }) => setReminderHoursBeforeStart(Number(value))}
                />
              )}
            </div>
          </>
        )}

        <div className={classes['create-event__channels']}>
          <Select
            label={t('common.channels_other')}
            items={channelSelectItems}
            selectedItemIds={channelIds}
            onMultiCheckChange={setChannelIds}
            disabled={!allowToEditChannels}
            staticListPosition
            search
          />
        </div>

        <div className={classes['create-event__buttons']}>
          {(!isEditMode || editStatus !== EventStatus.PUBLISHED) && (
            <ScheduleInput
              time={scheduledAt}
              setTime={setScheduledAt}
              maxDate={startAt}
              disabled={isScheduledDisabled}
              iconHint={ctxT('schedule')}
              title={ctxT('schedule')}
              summaryLabel={t('schedule.publishing')}
              confirmLabel={t('common.schedule')}
            />
          )}

          {!isEditMode && (
            <Button
              type={ButtonType.secondary}
              label={t('common.save-draft')}
              onClick={() => onCreate(EventStatus.UNPUBLISHED)}
              disabled={fileLoading || loading || Boolean(scheduledAt) || !channelIds.length}
            />
          )}

          {isEditMode && (
            <PublishToggle
              isPublished={editStatus === EventStatus.PUBLISHED}
              onClick={onPublishToggle}
              disabled={loading}
            />
          )}

          {primaryButton}
        </div>
      </div>
    );
  }, [
    allowToEditChannels,
    allowWaitlist,
    capacityLimitEnabled,
    channelIds,
    channelSelectItems,
    ctxT,
    descriptionContent,
    editStatus,
    endAt,
    gallery,
    hasEventTags,
    hostItemTemplate,
    hosts,
    hostsFetchQuery,
    isDeadlineDisabled,
    isEditMode,
    isExternalUrl,
    isScheduledDisabled,
    loading,
    externalLabel,
    externalUrl,
    externalUrlError,
    fileLoading,
    location.address,
    location.type,
    location.virtualUrl,
    name,
    onCreate,
    onExternalUrlBlur,
    onExternalUrlChange,
    onPublishToggle,
    onVirtualUrlBlur,
    onVirtualUrlChange,
    primaryButton,
    virtualUrlError,
    organisationTags,
    participantsCapacity,
    participantsRegistrationDeadlineEndAt,
    reminderEnabled,
    reminderHoursBeforeStart,
    scheduledAt,
    showParticipants,
    startAt,
    t,
    type,
  ]);

  return (
    <>
      <Modal
        isOpen={isOpened}
        body={body}
        onClose={onCloseHandler}
        ariaLabel={ctxT(`${isEditMode ? 'edit' : 'create'}-title`)}
        alignTop
        disableCloseByOverlay
      />

      <ConfirmationModal
        isOpen={showCloseConfirmation}
        title={ctxT('close-confirmation-title')}
        subTitle={ctxT('close-confirmation-subtitle')}
        acceptLabel={ctxT('close-confirmation-discard')}
        cancelLabel={ctxT('close-confirmation-back')}
        onAccept={onClose}
        onClose={() => setShowCloseConfirmation(false)}
        contentStyle={{ width: '26rem' }}
      />
    </>
  );
};
